import React, { useEffect, useState } from 'react';
import { FormGroup, HorizontalRule, Link } from 'arag-ui-lib';
import documentTypeProjections from '../../projections/documentType';
import codes from '../../utils/constants/codes';
import questionFunctions from '../../utils/functions/question';
import { useAppSelector } from '../../hooks/hooks';
import ProgressBar from '../general/progressBar/progressBar';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';

type Props = {
    attachment: {
        documentTypes: Array<any>;
        name: string;
        id: string;
        abortController: any;
        fileToMB: number;
    };
    handleTypeChange: (attachmentIndex: any) => any;
    uploadOnlyOnceDocumentTypesCodes: any;
    attachmentIndex: number | null | string;
    handleDownload: (attachment: {
        documentTypes: Array<any>;
        name: string;
    }) => any;
    removeAttachment: (attachmentIndex: number | string | null) => any;
    maxAttachmentsSizeInBytes: string | number | null;
    autoSaveAction: Function;
    fileUploadingProgress: any;
    cancelUpload: Function;
    isDownloadingFile: string | null;
    fileDownloadingProgress: any;
};
const AttachmentItem = (props: Props): JSX.Element => {
    const {
        attachment,
        handleTypeChange,
        uploadOnlyOnceDocumentTypesCodes,
        attachmentIndex,
        handleDownload,
        removeAttachment,
        maxAttachmentsSizeInBytes,
        autoSaveAction,
        fileUploadingProgress,
        cancelUpload,
        isDownloadingFile,
        fileDownloadingProgress,
    } = props;
    const { t } = useTranslation();
    const [invalidAttachmentsMessage, setInvalidAttachmentsMessage] = useState<
        string | null
    >(null);
    const documentTypesOptions = useAppSelector(
        documentTypeProjections.attachmentAvailableDocumentTypesOptions(
            attachment,
            uploadOnlyOnceDocumentTypesCodes,
            codes.claimAttachments,
        ),
    );
    const question = useAppSelector(
        state => state.question.question.byId['claim-attachments'],
    );

    // individual attachment validation
    useEffect(() => {
        const attachmentValidationError = questionFunctions.validateAttachment(
            attachment,
            question,
        );

        if (attachmentValidationError) {
            setInvalidAttachmentsMessage(attachmentValidationError);
            return;
        }

        setInvalidAttachmentsMessage(null);
    }, [
        attachment,
        setInvalidAttachmentsMessage,
        maxAttachmentsSizeInBytes,
        question,
    ]);

    return (
        <div key={attachmentIndex}>
            {attachment.name === '' ? null : (
                <>
                    <FormGroup
                        errorMessage={invalidAttachmentsMessage}
                        labelSize="col-sm-2"
                        inputSize="col-sm-6"
                        label={t('type')}
                        variant="multiSelect"
                        inputProps={{
                            instanceId: `selectIn_${attachmentIndex}`,
                            onChange: handleTypeChange(attachmentIndex),
                            options: documentTypesOptions,
                            value: attachment.documentTypes || [],
                            onBlur: autoSaveAction,
                            placeholder: i18n.t('placeholderTextForSelect'),
                        }}
                    />
                    <FormGroup
                        labelSize="col-sm-2"
                        inputSize="col-sm-6"
                        label=""
                        variant="plain"
                        inputProps={{
                            label: (
                                <a
                                    onClick={async e => {
                                        e.preventDefault();
                                        await handleDownload(attachment);
                                    }}
                                    aria-disabled={
                                        fileUploadingProgress?.find(
                                            (i: any) => i.id === attachment.id,
                                        )?.progress < 100
                                    }
                                >
                                    {attachment.name}
                                </a>
                            ),
                        }}
                    />
                    {isDownloadingFile === attachment?.id &&
                        !fileDownloadingProgress?.find(
                            (i: { id: string }) => i.id === attachment.id,
                        )?.id && (
                            <ProgressBar
                                progress={{
                                    id: isDownloadingFile as any,
                                    progress: 0,
                                }}
                                hasInitialStyle={true}
                                subProgressTitle={'Downloaded...'}
                                additionalStyles={'col-sm-offset-2'}
                            />
                        )}
                    {fileDownloadingProgress?.find(
                        (i: { id: string }) => i.id === attachment.id,
                    )?.id && (
                        <ProgressBar
                            hasInitialStyle={true}
                            subProgressTitle={'Downloaded...'}
                            progress={fileDownloadingProgress?.find(
                                (i: { id: string }) => i.id === attachment.id,
                            )}
                            additionalStyles={'col-sm-offset-2'}
                        />
                    )}

                    {fileUploadingProgress?.find(
                        (i: { id: string }) => i.id === attachment.id,
                    )?.id && (
                        <div className={'row'}>
                            <ProgressBar
                                progress={fileUploadingProgress?.find(
                                    (i: { id: string }) =>
                                        i.id === attachment.id,
                                )}
                                additionalStyles={'col-sm-2'}
                                mainStyles={'col-sm-6'}
                            />
                        </div>
                    )}

                    <div className="row">
                        <div className={'col-sm-12'}>
                            <div className="col-sm-2"></div>
                            <div className={'col-sm-6'}>
                                <Link
                                    disabled={
                                        fileUploadingProgress?.find(
                                            (i: any) => i.id === attachment.id,
                                        )?.progress < 100
                                    }
                                    variant="tertiary"
                                    arrowDirection="left"
                                    label={t('remove')}
                                    onClick={() =>
                                        removeAttachment(attachmentIndex)
                                    }
                                />
                            </div>
                            <div
                                className={'col-sm-2'}
                                style={{ marginLeft: '20px' }}
                            >
                                {attachment.abortController &&
                                    attachment.fileToMB > codes.thresholdMb &&
                                    fileUploadingProgress?.find(
                                        (i: any) => i.id === attachment.id,
                                    )?.progress < codes.progressEnd && (
                                        <Link
                                            variant="tertiary"
                                            arrowDirection="left"
                                            label={t('undo')}
                                            id={'cancelUpload'}
                                            onClick={() =>
                                                cancelUpload(
                                                    attachment?.id,
                                                    attachmentIndex,
                                                )
                                            }
                                        />
                                    )}
                            </div>
                        </div>
                    </div>
                    <HorizontalRule variant="pale" />
                </>
            )}
        </div>
    );
};

export default AttachmentItem;
